// frontend/src/components/DriverStats.js
import '../api/axiosConfig';
import React, { useEffect, useState } from 'react';
import axios from 'axios';
import { Modal, Button, Table, Badge, Row, Col, Card, Accordion } from 'react-bootstrap';

export default function DriverStats() {
  const [metrics, setMetrics] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [fy, setFy] = useState(null);
  const [emailTo, setEmailTo] = useState('');
  const [drillDownData, setDrillDownData] = useState(null);
  const [showDrillDown, setShowDrillDown] = useState(false);
  const [drillDownLoading, setDrillDownLoading] = useState(false);

  useEffect(() => {
    (async () => {
      try {
        setLoading(true);
        const { data } = await axios.get('/diary/metrics');
        setMetrics(data);
        
        const { data: fyData } = await axios.get('/diary/financial-report');
        setFy(fyData);
      } catch (err) {
        setError(`Failed to load metrics: ${err.response?.data?.message || err.message}`);
      } finally {
        setLoading(false);
      }
    })();
  }, []);

  const handleDrillDown = async (period, value, title) => {
    try {
      setDrillDownLoading(true);
      const response = await axios.get(`/diary/job-breakdown/${period}/${value}`);
      setDrillDownData({
        ...response.data,
        title,
        period,
        value
      });
      setShowDrillDown(true);
    } catch (error) {
      console.error('Error fetching drill-down data:', error);
      alert('Failed to load detailed breakdown');
    } finally {
      setDrillDownLoading(false);
    }
  };

  const formatPeriodDisplay = (period, value) => {
    if (period === 'week') {
      return `Week ${value}`;
    } else if (period === 'month') {
      const [year, month] = value.split('-');
      const monthNames = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'];
      return `${monthNames[parseInt(month) - 1]} ${year}`;
    } else if (period === 'year') {
      return value;
    }
    return value;
  };

  const renderTable = (title, series) => (
    <div className="card mb-3">
      <div className="card-header fw-semibold">{title}</div>
      <div className="card-body">
        <div className="row g-3">
          {['week','month','year'].map(grp => (
            <div key={grp} className="col-12 col-md-4">
              <h6 className="text-muted text-uppercase">{grp}</h6>
              {series?.[grp]?.length ? (
                <table className="table table-sm align-middle mb-0">
                  <thead>
                    <tr>
                      <th style={{width:'60%'}}>Period</th>
                      <th className="text-end">Count</th>
                      <th className="text-end" style={{width:'20%'}}>Action</th>
                    </tr>
                  </thead>
                  <tbody>
                    {series[grp].map(row => (
                      <tr key={row.period}>
                        <td>{row.period}</td>
                        <td className="text-end fw-semibold">{row.count}</td>
                        <td className="text-end">
                          <Button 
                            size="sm" 
                            variant="outline-primary"
                            onClick={() => handleDrillDown(grp, row.period, `${title} - ${formatPeriodDisplay(grp, row.period)}`)}
                            disabled={drillDownLoading}
                          >
                            {drillDownLoading ? '...' : '🔍'}
                          </Button>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              ) : (
                <div className="text-muted">No data</div>
              )}
            </div>
          ))}
        </div>
      </div>
    </div>
  );

  return (
    <div className="container mt-4">
      <div className="sticky-top bg-white border-bottom pb-3" style={{ zIndex: 1020, top: '56px' }}>
        <div className="container">
          <div className="d-flex justify-content-between align-items-center mb-2">
            <h4 className="mb-0">Driver Dashboard</h4>
          </div>
        </div>
      </div>

      <div className="container mt-4">
        {loading && <div className="spinner-border" role="status"/>}
        {error && <div className="alert alert-danger mt-3">{error}</div>}
        {!loading && !error && metrics && (
          <>
            <div className="d-flex gap-2 mb-3">
              <button className="btn btn-outline-secondary" onClick={() => window.print()}>
                Print
              </button>
              <div className="input-group" style={{maxWidth:'420px'}}>
                <span className="input-group-text">Email to</span>
                <input
                  type="email"
                  className="form-control"
                  placeholder="leave blank to send to your email"
                  value={emailTo}
                  onChange={e => setEmailTo(e.target.value)}
                />
                <button
                  className="btn btn-primary"
                  onClick={async () => {
                    try {
                      await axios.post('/diary/driver-report/email', { to: emailTo || undefined });
                      alert('Report email queued.');
                    } catch (e) {
                      alert('Failed to email report');
                    }
                  }}
                >
                  Email Report
                </button>
              </div>
            </div>
            {renderTable('Completed Jobs', metrics.completed)}
            {renderTable('Accepted Jobs (from Lead Generator)', metrics.accepted)}
            {renderTable('Future Jobs (Not Done Yet)', metrics.future)}
            {fy && (
              <div className="card mt-3">
                <div className="card-header fw-semibold">Financial Year {fy.financialYear} (April → March)</div>
                <div className="card-body">
                  <div className="table-responsive">
                    <table className="table table-sm align-middle">
                      <thead>
                        <tr>
                          <th>Month</th>
                          <th className="text-end">Jobs</th>
                          <th className="text-end">Earned (£)</th>
                        </tr>
                      </thead>
                      <tbody>
                        {fy.months.map(m => (
                          <tr key={m.period}>
                            <td>{m.label}</td>
                            <td className="text-end">{m.jobs}</td>
                            <td className="text-end">{m.earned.toFixed(0)}</td>
                          </tr>
                        ))}
                      </tbody>
                      <tfoot>
                        <tr className="table-active">
                          <th>Year to Date</th>
                          <th className="text-end">{fy.ytd.jobs}</th>
                          <th className="text-end">{fy.ytd.earned.toFixed(0)}</th>
                        </tr>
                      </tfoot>
                    </table>
                  </div>
                </div>
              </div>
            )}
          </>
        )}
      </div>

      {/* Drill-down Modal */}
      <Modal show={showDrillDown} onHide={() => setShowDrillDown(false)} size="xl">
        <Modal.Header closeButton>
          <Modal.Title>{drillDownData?.title || 'Job Breakdown'}</Modal.Title>
        </Modal.Header>
        <Modal.Body>
          {drillDownData && (
            <>
              {/* Summary Cards */}
              <Row className="mb-4">
                <Col md={3}>
                  <Card className="text-center">
                    <Card.Body>
                      <h5 className="text-primary">{drillDownData.summary.totalJobs}</h5>
                      <small className="text-muted">Total Jobs</small>
                    </Card.Body>
                  </Card>
                </Col>
                <Col md={3}>
                  <Card className="text-center">
                    <Card.Body>
                      <h5 className="text-success">£{drillDownData.summary.totalAmount.toFixed(2)}</h5>
                      <small className="text-muted">Total Amount</small>
                    </Card.Body>
                  </Card>
                </Col>
                <Col md={3}>
                  <Card className="text-center">
                    <Card.Body>
                      <h5 className="text-info">
                        {drillDownData.summary.totalJobs > 0 ? 
                          (drillDownData.summary.totalAmount / drillDownData.summary.totalJobs).toFixed(2) : 0}
                      </h5>
                      <small className="text-muted">Average per Job</small>
                    </Card.Body>
                  </Card>
                </Col>
                <Col md={3}>
                  <Card className="text-center">
                    <Card.Body>
                      <h5 className="text-warning">
                        {Object.keys(drillDownData.dailyBreakdown).length}
                      </h5>
                      <small className="text-muted">Active Days</small>
                    </Card.Body>
                  </Card>
                </Col>
              </Row>

              {/* Status and Payment Method Breakdown */}
              <Row className="mb-4">
                <Col md={6}>
                  <Card>
                    <Card.Header>By Status</Card.Header>
                    <Card.Body>
                      {Object.entries(drillDownData.summary.byStatus).map(([status, data]) => (
                        <div key={status} className="d-flex justify-content-between mb-2">
                          <Badge bg={status === 'completed' ? 'success' : 'primary'}>{status}</Badge>
                          <span>{data.count} jobs - £{data.total.toFixed(2)}</span>
                        </div>
                      ))}
                    </Card.Body>
                  </Card>
                </Col>
                <Col md={6}>
                  <Card>
                    <Card.Header>By Payment Method</Card.Header>
                    <Card.Body>
                      {Object.entries(drillDownData.summary.byPaymentMethod).map(([method, data]) => (
                        <div key={method} className="d-flex justify-content-between mb-2">
                          <Badge bg="secondary">{method}</Badge>
                          <span>{data.count} jobs - £{data.total.toFixed(2)}</span>
                        </div>
                      ))}
                    </Card.Body>
                  </Card>
                </Col>
              </Row>

              {/* Daily Breakdown */}
              <Card className="mb-4">
                <Card.Header>Daily Breakdown</Card.Header>
                <Card.Body>
                  <Accordion>
                    {Object.entries(drillDownData.dailyBreakdown)
                      .sort((a, b) => {
                        const dayOrder = ['Monday', 'Tuesday', 'Wednesday', 'Thursday', 'Friday', 'Saturday', 'Sunday'];
                        return dayOrder.indexOf(a[0]) - dayOrder.indexOf(b[0]);
                      })
                      .map(([dayName, dayData]) => (
                        <Accordion.Item key={dayName} eventKey={dayName}>
                          <Accordion.Header>
                            <div className="d-flex justify-content-between w-100 me-3">
                              <span>{dayName}</span>
                              <span className="text-muted">
                                {dayData.count} jobs - £{dayData.total.toFixed(2)}
                              </span>
                            </div>
                          </Accordion.Header>
                          <Accordion.Body>
                            <Table size="sm" striped>
                              <thead>
                                <tr>
                                  <th>Date & Time</th>
                                  <th>Customer</th>
                                  <th>Pickup</th>
                                  <th>Destination</th>
                                  <th>Amount</th>
                                  <th>Payment</th>
                                  <th>Status</th>
                                </tr>
                              </thead>
                              <tbody>
                                {dayData.jobs.map((job, index) => (
                                  <tr key={index}>
                                    <td>{new Date(job.dateTime).toLocaleString()}</td>
                                    <td>{job.customerName || 'N/A'}</td>
                                    <td className="text-truncate" style={{maxWidth: '150px'}} title={job.pickupAddress}>
                                      {job.pickupAddress || 'N/A'}
                                    </td>
                                    <td className="text-truncate" style={{maxWidth: '150px'}} title={job.destinationAddress}>
                                      {job.destinationAddress || 'N/A'}
                                    </td>
                                    <td className="text-end">£{job.amount.toFixed(2)}</td>
                                    <td>
                                      <Badge bg="secondary">{job.paymentMethod || 'Cash'}</Badge>
                                    </td>
                                    <td>
                                      <Badge bg={job.status === 'completed' ? 'success' : 'primary'}>
                                        {job.status}
                                      </Badge>
                                    </td>
                                  </tr>
                                ))}
                              </tbody>
                            </Table>
                          </Accordion.Body>
                        </Accordion.Item>
                      ))}
                  </Accordion>
                </Card.Body>
              </Card>
            </>
          )}
        </Modal.Body>
        <Modal.Footer>
          <Button variant="secondary" onClick={() => setShowDrillDown(false)}>
            Close
          </Button>
        </Modal.Footer>
      </Modal>
    </div>
  );
}

